import '../../App.css';
import axios from 'axios';
import { useEffect, useState, useRef } from 'react';
import Highlightable from 'highlightable';
import React from 'react';
import SelectionToolbar from '../SelectionToolbar';
import { logHighlightEvent } from '../../logging';
import { Tooltip } from '@adobe/react-spectrum';

function UsabilityRedactDocument({ ...properties }) {
    const [opacityAmount, setOpacityAmount] = useState(0);
    const [mouseIsDown, setMouseIsDown] = useState(false);
    const [mouseWidgetInfo, setMouseWidgetInfo] = useState({ x: 0, y: 0, show: false, value: 0 });
    const [tooltipOffset, setTooltipOffset] = useState({ x: 0, y: 0 });
    const [mouseWidgetInstructions, setMouseWidgetInstructions] = useState("More highlighting obscures text.");
    const [dummyWordCount, setDummyWordCount] = useState(0);
    const tooltipRef = useRef();
    const [potentialWordCount, setPotentialWordCount] = useState(0);


    function checkOverlappingRanges(left, right) {
        // the two ranges overlap
        if (right.start <= left.end) {
            return true
        }
        return false;
    }

    function mergeOverlappingRanges(left, right) {
        var newStart = Math.min(left.start, right.start);
        var newEnd = Math.max(left.end, right.end);
        var highlight = true;
        var showToolbar = false;

        // check whether the content should be highlighted right away
        if (properties.selectedTool !== "highlighter") {
            highlight = false;
            showToolbar = true;
        }

        let highlightedText = properties.passage.substring(newStart, newEnd + 1);

        return { start: newStart, end: newEnd, text: highlightedText, highlight: highlight, showToolbar: showToolbar };
    }

    function mergeAllHighlights(newHighlight) {
        var highlights = properties.highlights.concat(newHighlight).sort((a, b) => a.start - b.start);

        function addDataToHighlight(highlightToEdit) {
            var highlight = true;
            var showToolbar = false;

            // check whether the content should be highlighted right away
            if (properties.selectedTool !== "highlighter") {
                highlight = false;
                showToolbar = true;
            }
            highlightToEdit.highlight = highlight;
            highlightToEdit.showToolbar = showToolbar;
        }

        for (var i = 0; i < highlights.length - 1; i++) {
            var left = highlights[i];
            var right = highlights[i + 1];
            if (checkOverlappingRanges(left, right)) {
                var merged = mergeOverlappingRanges(left, right);
                highlights.splice(i, 2, merged);
                i--;
            }
        }

        for (var i = 0; i < highlights.length; i++) {
            if (highlights[i].highlight === undefined || highlights[i].showToolbar === undefined) {
                addDataToHighlight(highlights[i]);
            }
        }


        return highlights;
    }

    function getHighlightedText(highlights) {
        var text = "";
        for (var i = 0; i < highlights.length; i++) {
            text += highlights[i].text + " ";
        }
        return text;
    }

    function checkRangeValid(range) {
        var rangeContents = document.getSelection().getRangeAt(0).cloneContents();
        var allSpans = rangeContents.querySelectorAll('span');
        var selectionSpans = [];
        allSpans.forEach((span) => {
            if (span.getAttribute('data-position') !== null) {
                selectionSpans.push(span);
            }
        });

        var startSpan = selectionSpans[0];
        var endSpan = selectionSpans[selectionSpans.length - 1];
        if (startSpan === undefined || endSpan === undefined) {
            return range;
        }

        // first check for any NaN values
        if (isNaN(range.start) || isNaN(range.end)) {
            var startIndex = parseInt(startSpan.getAttribute('data-position'));
            var endIndex = parseInt(endSpan.getAttribute('data-position'));
            range.start = startIndex;
            range.end = endIndex;
            range.text = properties.passage.substring(startIndex, endIndex + 1);
        }
        return range;
    }

    function mapRange(value, originalMin, originalMax, newMin, newMax) {
        let mappedValue = newMin + ((value - originalMin) * (newMax - newMin)) / (originalMax - originalMin);
        mappedValue = Math.min(newMax, Math.max(newMin, mappedValue));
        return mappedValue;
    }

    function onTextHighlighted() {
        var sel = document.getSelection();
        var unsnappedStartIndex = parseInt(sel.anchorNode.parentNode.getAttribute('data-position'));
        var unsnappedEndIndex = parseInt(sel.focusNode.parentNode.getAttribute('data-position'));
        var unsnappedText = properties.passage.substring(unsnappedStartIndex, unsnappedEndIndex + 1);
        var unsnappedRange = { start: unsnappedStartIndex, end: unsnappedEndIndex, text: unsnappedText };

        var wordSelection = snapSelectionToWord();
        var startNode = wordSelection.anchorNode.parentNode;
        var endNode = wordSelection.focusNode.parentNode;

        var startIndex = parseInt(startNode.getAttribute('data-position'));
        var endIndex = parseInt(endNode.getAttribute('data-position'));
        var leftToRight = startIndex < endIndex;
        // swap order if not left to right
        if (!leftToRight) {
            var tmpNode = endNode;
            endNode = startNode;
            startNode = tmpNode;

            var tmpIndex = endIndex;
            endIndex = startIndex;
            startIndex = tmpIndex;
        }

        var snappedRange = { start: startIndex, end: endIndex, text: properties.passage.substring(startIndex, endIndex + 1) };
        checkRangeValid(snappedRange);
        var mergedHighlights = mergeAllHighlights(snappedRange);
        var highlightedText = getHighlightedText(mergedHighlights);
        var newWordCount = properties.countNumWords(highlightedText);
        snappedRange.totalWordCount = newWordCount; // store the total word count in the range, useful recalculate the blur amount when a highlight is deleted

        var validHighlight = true;

        if (newWordCount <= properties.maxWordCount || !properties.constrainHighlights) {
            // do not change the word count with the cursor tool, otherwise you double count
            if (properties.selectedTool === "highlighter") {
                properties.setWordCount(newWordCount);
            }
            properties.setHighlights(mergedHighlights);
            properties.setShowError(false);
            document.getSelection().removeAllRanges();
            validHighlight = true;
        }

        else if (newWordCount > properties.maxWordCount) {
            properties.setShowError(true);
            validHighlight = false;
        }

        var log = {
            unsnappedRange: unsnappedRange,
            snappedRange: snappedRange,
            participantId: properties.participantId,
            validHighlight: validHighlight,
            newWordCount: newWordCount,
            tool: properties.selectedTool,
            type: "add"
        }

        // logHighlightEvent(log);
    }

    // helper function to snap the selection to words - https://stackoverflow.com/questions/7380190/select-whole-word-with-getselection
    function snapSelectionToWord() {
        var sel;

        // Check for existence of window.getSelection() and that it has a
        // modify() method. IE 9 has both selection APIs but no modify() method.
        if (window.getSelection && (sel = window.getSelection()).modify) {
            sel = window.getSelection();
            if (!sel.isCollapsed) {
                // Detect if selection is backwards
                var range = document.createRange();
                range.setStart(sel.anchorNode, sel.anchorOffset);
                range.setEnd(sel.focusNode, sel.focusOffset);
                var backwards = range.collapsed;
                range.detach();

                // modify() works on the focus of the selection
                var endNode = sel.focusNode,
                    endOffset = sel.focusOffset;
                sel.collapse(sel.anchorNode, sel.anchorOffset);

                var direction = [];
                if (backwards) {
                    direction = ['backward', 'forward'];
                } else {
                    direction = ['forward', 'backward'];
                }

                sel.modify("move", direction[0], "character");
                sel.modify("move", direction[1], "word");
                sel.extend(endNode, endOffset);
                sel.modify("extend", direction[1], "character");
                sel.modify("extend", direction[0], "word");

            }
        } else if ((sel === document.selection) && sel.type !== "Control") {
            var textRange = sel.createRange();
            if (textRange.text) {
                textRange.expand("word");
                // Move the end back to not include the word's trailing space(s),
                // if necessary
                while (/\s$/.test(textRange.text)) {
                    textRange.moveEnd("character", -1);
                }
                textRange.select();
            }
        }

        return sel;
    }

    function filterSelections(element) {
        return element.highlight;
    }

    // helper function to count the number of potential words after a selection, without double counting existing highlights
    function countNumPotentialWords() {
        var sel = document.getSelection();
        var unsnappedStartIndex = parseInt(sel.anchorNode.parentNode.getAttribute('data-position'));
        var unsnappedEndIndex = parseInt(sel.focusNode.parentNode.getAttribute('data-position'));
        var unsnappedText = properties.passage.substring(unsnappedStartIndex, unsnappedEndIndex + 1);
        var unsnappedRange = { start: unsnappedStartIndex, end: unsnappedEndIndex, text: unsnappedText };

        var wordSelection = snapSelectionToWord();
        var startNode = wordSelection.anchorNode.parentNode;
        var endNode = wordSelection.focusNode.parentNode;

        var startIndex = parseInt(startNode.getAttribute('data-position'));
        var endIndex = parseInt(endNode.getAttribute('data-position'));
        var leftToRight = startIndex < endIndex;
        // swap order if not left to right
        if (!leftToRight) {
            var tmpNode = endNode;
            endNode = startNode;
            startNode = tmpNode;

            var tmpIndex = endIndex;
            endIndex = startIndex;
            startIndex = tmpIndex;
        }

        var snappedRange = { start: startIndex, end: endIndex, text: properties.passage.substring(startIndex, endIndex + 1) };
        checkRangeValid(snappedRange);
        var mergedHighlights = mergeAllHighlights(snappedRange);
        var highlightedText = getHighlightedText(mergedHighlights);
        var newWordCount = properties.countNumWords(highlightedText);

        return newWordCount;
    }

    // filter out the selections that are not highlighted
    function mouseDown(e) {
        if (e.target.className !== "blackButton") {
            // hide temporary highlights
            var filteredHighlights = properties.highlights.filter(filterSelections);

            // hide all black buttons
            for (var i = 0; i < filteredHighlights.length; i++) {
                filteredHighlights[i].showToolbar = false;
            }

            properties.setHighlights(filteredHighlights);
            properties.setShowError(false);
        }

        // don't want the effect if they are clicking on an actual highlight (to delete it)
        if (e.target.parentNode.className !== "highlight" && e.target.className !== "highlight" && e.target.className !== "blackButton") {
            console.log(e.target.parentNode.className);
            setMouseIsDown(true);
        }

    }

    function mouseUp(e) {
        setMouseIsDown(false);
        setMouseWidgetInfo({ x: 0, y: 0, show: false, value: 0 });
    }

    function mouseMove(e) {
        let textSelection = document.getSelection().toString();
        setMouseWidgetInfo({ x: e.clientX, y: e.clientY });

        // determine if they need to wait at all
        if (textSelection.length > 0 && textSelection !== "" && mouseIsDown) {
            let newWordCount = countNumPotentialWords();
            setPotentialWordCount(newWordCount);
            setDummyWordCount(newWordCount);

            // TODO: adjust the threshold for the tooltip
            // if we keep it consistent, no need to do the mapping
            // always show the tooltip for this experiment
            let opacityAmount = mapRange(newWordCount, 0, 650, 0, 1);
            setOpacityAmount(opacityAmount);
            setMouseWidgetInfo({ x: e.clientX, y: e.clientY, show: true });


        }
    }

    useEffect(() => {
        if (tooltipRef.current) {
            let tooltip = tooltipRef.current.UNSAFE_getDOMNode();
            let bounds = tooltip.getBoundingClientRect();
            setTooltipOffset({ x: bounds.width / 2, y: bounds.height + 20 });
        }
    }, [mouseWidgetInstructions]);

    return (
        <>
            <Tooltip UNSAFE_className="no-select" ref={tooltipRef} isOpen={mouseWidgetInfo.show} left={mouseWidgetInfo.x - tooltipOffset.x} top={mouseWidgetInfo.y - tooltipOffset.y} placement='top' position={"fixed"} zIndex={10000} UNSAFE_style={{ textAlign: "center", display: "unset" }}>{mouseWidgetInstructions}</Tooltip>
            <div id="textToRead" className='tweakingDocumentReader' onMouseDown={e => mouseDown(e)} onCopy={e => e.preventDefault()} onMouseMove={(e) => mouseMove(e)} onMouseUp={(e) => mouseUp(e)}>
                <Highlightable
                    ranges={properties.highlights}
                    enabled={properties.maxWordCount === 0 ? false : true} // disable highlighting if the max word count is 0
                    highlightStyle={range => {
                        if (range.highlight) {
                            return {
                                backgroundColor: '#FDEAA3',
                            }
                        }
                        else {
                            return {
                                backgroundColor: '#b3d8ff',
                            }
                        }
                    }}
                    onTextHighlighted={onTextHighlighted}
                    text={properties.passage}
                    nodeRenderer={(index, range, text) => {
                        let currChar = text.charAt(index);

                        // check if the character is part of the text the participant has to highlight
                        let needToHighlight = false;
                        properties.textToHighlight.forEach((highlight) => {
                            if (index >= highlight.start && index <= highlight.end) {
                                needToHighlight = true;
                            }
                        });

                        if (currChar === '\n') {
                            return (<><br /><br /></>)
                        }
                        // if the character is part of the text the participant needs to highight, add a background to it.
                        else if (needToHighlight) {
                            if (mouseIsDown) {
                                return (
                                    <span data-position={index} range={range} style={{ backgroundColor: "rgba(0, 0, 0," + opacityAmount + ")", color: "rgba(0, 0, 0," + (1 - opacityAmount) + ")", textDecoration: 'underline', textDecorationColor: 'blue', textDecorationThickness: '2px' }}>{currChar}</span>
                                )
                            }
                            return (
                                <span data-position={index} range={range} style={{ textDecoration: 'underline', textDecorationColor: 'blue', textDecorationThickness: '2px' }}>{currChar}</span>
                            )
                        }
                        if (mouseIsDown) {
                            return (<span style={{"--lightness": (mapRange(opacityAmount, 0, 1, 0, 86.87)) + "%", "--transparency": (1-opacityAmount), backgroundColor: "rgba(0, 0, 0," + opacityAmount + ")", color: "rgba(0, 0, 0," + (1-opacityAmount) + ")"}} data-position={index} range={range}>{currChar}</span>)
                        }

                        return (
                            <span data-position={index} range={range}>{currChar}</span>
                        )
                    }}
                    rangeRenderer={range => {
                        // helper function to update the tool bar visibility
                        function updateShowToolbar(newValue) {
                            var index = properties.highlights.indexOf(range[0].props.range);
                            var newHighlight = properties.highlights[index];
                            newHighlight.showToolbar = newValue;
                            properties.setHighlights(properties.highlights.toSpliced(index, 1).concat(newHighlight));
                        }

                        function mouseEnter(e) {
                            e.style.outline = "2px solid #337AC2";
                            e.style.cursor = "pointer";
                        }

                        function mouseLeave(e) {
                            if (range[0].props.range.highlight)
                                e.style.outline = "2px solid #FDEAA3";
                            else
                                e.style.outline = "2px solid #b3d8ff";
                            e.style.cursor = "auto";
                        }

                        function mouseClick(e) {
                            updateShowToolbar(true);
                        }

                        // creating an actual highlight
                        if (range[0].props.range.highlight) {
                            return (
                                <div style={{ display: 'inline', position: 'relative' }}>
                                    <SelectionToolbar
                                        recalculateBlurWhenDeleted={true}
                                        passage={properties.passage}
                                        countNumWords={properties.countNumWords}

                                        wordCount={properties.wordCount}
                                        targetWordCount={properties.targetWordCount}
                                        setWordCount={properties.setWordCount}
                                        setProgressWordCount={properties.setProgressWordCount}
                                        setOpacityAmount={setOpacityAmount}

                                        selectedTool={'highlighter'}
                                        updateShowToolbar={updateShowToolbar}
                                        participantId={properties.participantId}

                                        range={range}
                                        highlights={properties.highlights}
                                        setHighlights={properties.setHighlights} />
                                    <span className='highlight' style={{ outline: "2px solid #FDEAA3", backgroundColor: "#FDEAA3" }} onMouseEnter={e => mouseEnter(e.target.parentNode)} onMouseLeave={e => mouseLeave(e.target.parentNode)} onClick={e => mouseClick(e.target.parentNode)}>{range}</span>
                                </div>
                            );

                        }

                        // creating a fake selection effect
                        else {
                            return (
                                <div style={{ display: 'inline', position: 'relative' }}>
                                    <SelectionToolbar
                                        passage={properties.passage}
                                        countNumWords={properties.countNumWords}

                                        wordCount={properties.wordCount}
                                        setWordCount={properties.setWordCount}
                                        setProgressWordCount={properties.setProgressWordCount}
                                        setShadowWordCount={properties.setShadowWordCount}

                                        selectedTool={'cursor'}
                                        updateShowToolbar={updateShowToolbar}

                                        range={range}

                                        highlights={properties.highlights}
                                        setHighlights={properties.setHighlights} />
                                    <span className='fakeHighlight' style={{ outline: "2px solid #b3d8ff", backgroundColor: "#b3d8ff" }}>{range}</span>
                                </div>)
                                ;
                        }
                    }
                    } />

            </div>
        </>
    );
}

export default UsabilityRedactDocument;